import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { clearContentCache } from "@/lib/useContent";
import { formatApiError } from "@/context/AuthContext";
import {
    Save, RotateCcw, Plus, Trash2, Edit3, Check, X,
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon, Upload, Navigation, UserCheck, Radio, CalendarClock, Ban, MessageSquareQuote, Star, Mic, Play,
} from "lucide-react";
import { Field } from "../shared.jsx";
import { REGIONS, EMPTY_VIDEO } from "../constants.js";

export default function VideosEditor() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null or video object (with id for edit, without for create)
    const [bulk, setBulk] = useState(false);

    const load = () => api.get("/videos").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async (data) => {
        try {
            if (data.id) {
                await api.put(`/admin/videos/${data.id}`, data);
                toast.success("Video updated.");
            } else {
                await api.post("/admin/videos", data);
                toast.success("Video added.");
            }
            setEditing(null);
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this video?")) return;
        try {
            await api.delete(`/admin/videos/${id}`);
            toast.success("Deleted.");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    if (bulk) {
        return <BulkImport onDone={() => { setBulk(false); load(); }} onCancel={() => setBulk(false)} />;
    }

    if (editing !== null) {
        return <VideoForm initial={editing} onSave={save} onCancel={() => setEditing(null)} />;
    }

    return (
        <div data-testid="videos-editor">
            <div className="flex justify-end gap-2 mb-6">
                <button onClick={() => setBulk(true)} className="btn-outline text-sm" style={{padding: "0.55rem 1rem"}} data-testid="admin-bulk-import">
                    <Plus size={14} /> Bulk import from YouTube
                </button>
                <button onClick={() => setEditing({ ...EMPTY_VIDEO })} className="btn-primary" data-testid="admin-add-video">
                    <Plus size={16} /> Add video
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.map((v) => (
                    <div key={v.id} className="card p-5 flex gap-4" data-testid={`admin-video-${v.id}`}>
                        {v.thumb && <img src={v.thumb} alt={v.title} className="w-28 aspect-video object-cover" />}
                        <div className="flex-1 min-w-0">
                            <div className="overline mb-1">{v.region} · {v.duration}</div>
                            <div className="font-serif text-lg truncate">{v.title}</div>
                            <div className="flex gap-2 mt-3 text-xs">
                                {v.featured && <span className="border border-[var(--sb-brand)] text-[var(--sb-brand)] px-2 py-0.5">Featured</span>}
                                {v.exclusive && <span className="border border-[var(--sb-blue)] text-[var(--sb-blue)] px-2 py-0.5">Exclusive</span>}
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <button onClick={() => setEditing(v)} className="text-[var(--sb-text-dim)] hover:text-[var(--sb-brand)] p-1" data-testid={`admin-video-edit-${v.id}`}><Edit3 size={16} /></button>
                            <button onClick={() => remove(v.id)} className="text-[var(--sb-text-dim)] hover:text-red-400 p-1" data-testid={`admin-video-delete-${v.id}`}><Trash2 size={16} /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function BulkImport({ onDone, onCancel }) {
    const [links, setLinks] = useState("");
    const [region, setRegion] = useState("Highlands");
    const [featured, setFeatured] = useState(false);
    const [exclusive, setExclusive] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        if (!links.trim()) {
            toast.error("Paste at least one YouTube link.");
            return;
        }
        setBusy(true);
        setResult(null);
        try {
            const { data } = await api.post("/admin/videos/bulk_import", {
                links, region, featured, exclusive,
            });
            setResult(data);
            const s = data.summary;
            toast.success(`Added ${s.added}, skipped ${s.duplicates}, failed ${s.failed}.`);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const lineCount = links.split("\n").filter((l) => l.trim()).length;

    return (
        <div className="card p-8 max-w-3xl" data-testid="bulk-import-form">
            <h2 className="font-serif text-2xl mb-2">Bulk import from YouTube</h2>
            <p className="text-[var(--sb-text-dim)] text-sm mb-6">
                Paste one YouTube link (or 11-character ID) per line. Titles are auto-fetched. Duplicates are skipped.
            </p>

            <div className="space-y-5">
                <Field label={`Links (${lineCount} ${lineCount === 1 ? "line" : "lines"})`}>
                    <textarea
                        rows={10}
                        className="input-line font-mono text-sm"
                        placeholder={"https://youtu.be/dQw4w9WgXcQ\nhttps://youtube.com/watch?v=ddlJNxVh1mU\nddlJNxVh1mU"}
                        value={links}
                        onChange={(e) => setLinks(e.target.value)}
                        data-testid="bulk-import-links"
                    />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Field label="Apply region to all">
                        <select className="input-line bg-[var(--sb-surface)]" value={region} onChange={(e) => setRegion(e.target.value)} data-testid="bulk-import-region">
                            {REGIONS.map((r) => <option key={r}>{r}</option>)}
                        </select>
                    </Field>
                    <label className="flex items-center gap-3 text-sm text-[var(--sb-text-dim)] self-end pb-2">
                        <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} data-testid="bulk-import-featured" /> Mark all featured
                    </label>
                    <label className="flex items-center gap-3 text-sm text-[var(--sb-text-dim)] self-end pb-2">
                        <input type="checkbox" checked={exclusive} onChange={(e) => setExclusive(e.target.checked)} data-testid="bulk-import-exclusive" /> Mark all subscriber-only
                    </label>
                </div>
            </div>

            {result && (
                <div className="mt-6 space-y-2 text-sm" data-testid="bulk-import-result">
                    {result.created.length > 0 && (
                        <div className="text-[var(--sb-brand)]">Added {result.created.length}:</div>
                    )}
                    <ul className="text-[var(--sb-text-dim)] space-y-1 max-h-40 overflow-auto">
                        {result.created.map((c) => <li key={c.id}>✓ {c.title} ({c.video_id})</li>)}
                        {result.skipped.map((s) => <li key={s.video_id}>↺ Duplicate: {s.title} ({s.video_id})</li>)}
                        {result.errors.map((e, i) => <li key={i} className="text-red-400">✗ {e.input}: {e.reason}</li>)}
                    </ul>
                </div>
            )}

            <div className="flex justify-end gap-2 mt-8">
                <button onClick={onCancel} className="btn-outline text-sm" style={{padding: "0.55rem 1rem"}}><X size={14} /> {result ? "Close" : "Cancel"}</button>
                {!result && (
                    <button onClick={run} disabled={busy} className="btn-primary text-sm" style={{padding: "0.55rem 1rem"}} data-testid="bulk-import-run">
                        <Check size={14} /> {busy ? "Importing…" : `Import ${lineCount || ""}`}
                    </button>
                )}
                {result && (
                    <button onClick={onDone} className="btn-primary text-sm" style={{padding: "0.55rem 1rem"}} data-testid="bulk-import-done">
                        <Check size={14} /> Done
                    </button>
                )}
            </div>
        </div>
    );
}


function VideoForm({ initial, onSave, onCancel }) {
    const [form, setForm] = useState(initial);

    return (
        <div className="card p-8 max-w-3xl" data-testid="video-form">
            <h2 className="font-serif text-2xl mb-6">{form.id ? "Edit video" : "New video"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Title"><input className="input-line" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="video-form-title" /></Field>
                <Field label="Region">
                    <select className="input-line bg-[var(--sb-surface)]" value={form.region} onChange={(e) => setForm({...form, region: e.target.value})} data-testid="video-form-region">
                        {REGIONS.map((r) => <option key={r}>{r}</option>)}
                    </select>
                </Field>
                <Field label="YouTube ID"><input className="input-line" value={form.video_id} onChange={(e) => setForm({...form, video_id: e.target.value})} placeholder="e.g. dQw4w9WgXcQ" data-testid="video-form-yt" /></Field>
                <Field label="Duration"><input className="input-line" value={form.duration} onChange={(e) => setForm({...form, duration: e.target.value})} placeholder="3:42" /></Field>
                <Field label="Thumbnail URL" full><input className="input-line" value={form.thumb} onChange={(e) => setForm({...form, thumb: e.target.value})} /></Field>
                <Field label="Summary" full><textarea className="input-line" rows={3} value={form.summary} onChange={(e) => setForm({...form, summary: e.target.value})} /></Field>
                <Field label="Latitude"><input type="number" step="0.0001" className="input-line" value={form.lat} onChange={(e) => setForm({...form, lat: parseFloat(e.target.value)})} /></Field>
                <Field label="Longitude"><input type="number" step="0.0001" className="input-line" value={form.lng} onChange={(e) => setForm({...form, lng: parseFloat(e.target.value)})} /></Field>
                <label className="flex items-center gap-3 text-sm text-[var(--sb-text-dim)]">
                    <input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})} data-testid="video-form-featured" /> Featured on homepage
                </label>
                <label className="flex items-center gap-3 text-sm text-[var(--sb-text-dim)]">
                    <input type="checkbox" checked={form.exclusive} onChange={(e) => setForm({...form, exclusive: e.target.checked})} data-testid="video-form-exclusive" /> Subscriber-only
                </label>
            </div>
            <div className="flex justify-end gap-2 mt-8">
                <button onClick={onCancel} className="btn-outline text-sm" style={{padding: "0.55rem 1rem"}}><X size={14} /> Cancel</button>
                <button onClick={() => onSave(form)} className="btn-primary text-sm" style={{padding: "0.55rem 1rem"}} data-testid="video-form-save"><Check size={14} /> Save video</button>
            </div>
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/* Blog editor                                                                */
